import React, { useEffect, useState } from 'react';
import { Autocomplete, ListItemText, TextField } from '@mui/material';
import { useDebounce } from 'use-debounce';
import { Client } from '../http';
import { BuildingSummary } from '../features/buildingEditor/buildingEditor';

// Autocomplete over the building editor list endpoint (tokenised fuzzy
// search server-side). Standalone axios call so the redux list state stays
// untouched. onInputChange lets a host page reuse the typed text, e.g. to
// filter a results grid alongside the suggestion dropdown.
const BuildingSearchField = ({
  label,
  excludeIds = [],
  onPick,
  onInputChange = undefined,
}: {
  label: string;
  excludeIds?: string[];
  onPick: (building: BuildingSummary) => void;
  onInputChange?: (value: string) => void;
}) => {
  const [input, setInput] = useState('');
  const [debouncedInput] = useDebounce(input, 400);
  const [options, setOptions] = useState<BuildingSummary[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!debouncedInput.trim()) {
      setOptions([]);
      return;
    }
    setLoading(true);
    Client.get<{ items: BuildingSummary[] }>('/api/v1/admin/buildings', {
      params: { search: debouncedInput, limit: 20 },
    })
      .then((response) => {
        setOptions((response.data.items || []).filter((b) => !excludeIds.includes(b.id)));
      })
      .catch(() => setOptions([]))
      .finally(() => setLoading(false));
  }, [debouncedInput]);

  return (
    <Autocomplete
      size="small"
      options={options}
      loading={loading}
      inputValue={input}
      onInputChange={(_, value) => {
        setInput(value);
        if (onInputChange) onInputChange(value);
      }}
      value={null}
      onChange={(_, value) => {
        if (value) {
          onPick(value);
          setInput('');
          if (onInputChange) onInputChange('');
        }
      }}
      getOptionLabel={(option) => option.address || option.id}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      filterOptions={(x) => x}
      noOptionsText={debouncedInput.trim() ? 'No matches' : 'Type to search'}
      renderInput={(params) => <TextField {...params} label={label} />}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <ListItemText
            primary={option.address}
            secondary={`${option.uniqueCode || 'no code'} · ${option.status || 'no status'}`}
          />
        </li>
      )}
    />
  );
};

export default BuildingSearchField;
